import { useClientPortalAPI } from "@certn/api/src/useClientPortalAPI";
import {
  Divider,
  Grid,
  GridItem,
  Layout,
  mq,
  MediaQuery,
  Loader,
} from "@certn/cascadia-react";
import { formatDate, parseName } from "@certn/utils";
import { css } from "@emotion/css";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import { AboutCard } from "./components/AboutCard";
import { CaseHistoryCard } from "./components/CaseHistoryCard";
import { PersonProfileTitleCard } from "./components/PersonProfileTitleCard";
import { ConfigurationError } from "../../components/ConfigurationError";
import { AppFooterLinks } from "../../components/app-footer/AppFooterLinks";
import { AppHeader } from "../../components/app-header/AppHeader";
import { AppNavigation } from "../../components/app-navigation/AppNavigation";
import { usePermissions } from "../../components/permissions/PermissionsProvider";

export function PeopleProfilePage() {
  const { subjectID } = useParams();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const { t } = useTranslation();

  const {
    data,
    isLoading,
    mutate: refreshCases,
  } = useClientPortalAPI(`/api/internal/client-portal/persons/${subjectID}/`);

  const { data: rescreenCases, mutate: refreshRescreen } = useClientPortalAPI(
    `/api/internal/client-portal/persons/${subjectID}/rescreens/`
  );

  const userHasPermission =
    hasPermission({
      action: "read",
      target: "subject",
      scope: "all",
    }) &&
    (hasPermission({
      action: "read",
      target: "case",
      scope: "all",
    }) ||
      hasPermission({
        action: "read",
        target: "case",
        scope: "my_groups",
      }));

  const viewCase = (caseID: string) => {
    navigate(`/case-details/${caseID}`);
  };

  const viewPerson = (personID: string) => {
    navigate(`/profiles/${personID}`);
  };

  const refreshData = () => {
    refreshCases();
    refreshRescreen();
  };

  if (!userHasPermission) {
    return (
      <ConfigurationError
        message={t(
          "Hmm, it looks like you do not have permission to use this feature."
        )}
      />
    );
  }

  return (
    <>
      <MediaQuery type="hide" sm>
        <Layout
          layout="full-width"
          header={<AppHeader />}
          navigation={<AppNavigation />}
          footer={<AppFooterLinks />}
          contentHeader={
            <PersonProfileTitleCard
              name={parseName(data?.latest_name)}
              email={data?.latest_email}
              lastOrder={formatDate(data?.last_order_date, "PP")}
            />
          }
          testID="profile-page-layout"
          backgroundColor="surface-sunken"
        >
          {!isLoading ? (
            <>
              <MediaQuery type="hide" md lg>
                <Grid layout="wide" className={styles.gridStyle}>
                  <GridItem span={8}>
                    <CaseHistoryCard
                      cases={data?.cases}
                      viewCase={viewCase}
                      rescreenCases={rescreenCases?.scheduled_rescreens}
                      refreshData={refreshData}
                    />
                  </GridItem>
                  <GridItem span={4}>
                    <AboutCard cases={data?.cases} viewPerson={viewPerson} />
                  </GridItem>
                </Grid>
              </MediaQuery>
              <MediaQuery md lg>
                <Grid layout="wide" className={styles.gridStyle}>
                  <GridItem span={12}>
                    <CaseHistoryCard
                      cases={data?.cases}
                      viewCase={viewCase}
                      rescreenCases={rescreenCases?.scheduled_rescreens}
                      refreshData={refreshData}
                    />
                  </GridItem>
                </Grid>
                <div style={{ marginTop: "var(--space-5)" }}>
                  <AboutCard cases={data?.cases} viewPerson={viewPerson} />
                </div>
              </MediaQuery>
            </>
          ) : (
            <div className={styles.empty}>
              <Loader />
            </div>
          )}
        </Layout>
      </MediaQuery>
      <MediaQuery sm>
        <>
          <AppHeader />
          <Divider />
          <PersonProfileTitleCard
            name={parseName(data?.latest_name)}
            email={data?.latest_email}
            lastOrder={formatDate(data?.last_order_date, "PP")}
          />

          {!isLoading ? (
            <>
              <div className={styles.mobileContent}>
                <CaseHistoryCard
                  cases={data?.cases}
                  rescreenCases={rescreenCases?.scheduled_rescreens}
                  viewCase={viewCase}
                  refreshData={refreshData}
                />
                <AboutCard cases={data?.cases} viewPerson={viewPerson} />
              </div>
            </>
          ) : (
            <div className={styles.empty}>
              <Loader />
            </div>
          )}
        </>
      </MediaQuery>
    </>
  );
}

const styles = {
  pageContent: css(
    {
      maxWidth: "1200px",
      display: "flex",
      flexDirection: "row",
      flex: 1,
      paddingTop: "var(--space-5)",
      maxHeight: "100%",
      overflow: "hidden",
      gap: "var(--space-5)",
    },
    mq({
      flexDirection: ["column", "row"],
    })
  ),
  gridStyle: css({
    gap: "var(--space-5)",
    maxWidth: "1200px",
  }),
  mobileContent: css({
    display: "flex",
    flexDirection: "column",
    backgroundColor: "var(--surface-sunken)",
    paddingTop: "var(--space-5)",
    paddingBottom: "var(--space-5)",
    gap: "var(--space-5)",
  }),
  empty: css({
    padding: "var(--space-6)",
    minHeight: 288,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "column",
    gap: "var(--space-4)",
  }),
};
